import { useEffect, useMemo, useState } from 'react'
import { BookOpen, ChevronRight, FileText, Image as ImageIcon, PlayCircle, RotateCcw, SearchX, SlidersHorizontal } from 'lucide-react'
import axios from 'axios'
import TechniqueModal from '../components/TechniqueModal'
import PageHeader from '../components/PageHeader'
import type { Technique } from '../types/Technique'

const beltLabels: Record<string, string> = {
  BLANCHE: 'Blanche',
  JAUNE: 'Jaune',
  ORANGE: 'Orange',
  VERTE: 'Verte',
  BLEUE: 'Bleue',
  MARRON: 'Marron',
  NOIRE: 'Noire',
}

const beltClasses: Record<string, string> = {
  BLANCHE: 'belt-white',
  JAUNE: 'belt-yellow',
  ORANGE: 'belt-orange',
  VERTE: 'belt-green',
  BLEUE: 'belt-blue',
  MARRON: 'belt-brown',
  NOIRE: 'belt-black',
}

const techniqueLabels: Record<string, string> = {
  KIHON: 'Kihon',
  KATA: 'Kata',
  SELF_DEFENSE: 'Self-défense',
  BASES: 'Bases',
  CHUTES: 'Chutes',
  AUTRE: 'Autre',
}

const beltOptions = Object.entries(beltLabels).map(([value, label]) => ({ value, label }))
const techniqueTypeOptions = Object.entries(techniqueLabels).map(([value, label]) => ({ value, label }))

const toggleSelection = (selection: Set<string>, value: string) => {
  const nextSelection = new Set(selection)
  if (nextSelection.has(value)) nextSelection.delete(value)
  else nextSelection.add(value)
  return nextSelection
}

const Techniques = () => {
  const [techniques, setTechniques] = useState<Technique[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTechnique, setSelectedTechnique] = useState<Technique | null>(null)
  const [selectedBelts, setSelectedBelts] = useState<Set<string>>(() => new Set())
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const fetchTechniques = async () => {
      try {
        const response = await axios.get<Technique[]>('/api/techniques')
        setTechniques(response.data)
      } catch (error) {
        console.error('Erreur lors du chargement des techniques:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTechniques()
  }, [])

  const getBeltLabel = (beltLevel: string) => beltLabels[beltLevel] || beltLevel
  const getBeltClass = (beltLevel: string) => beltClasses[beltLevel] || 'belt-white'
  const getTypeLabel = (type: string) => techniqueLabels[type] || type

  const filteredTechniques = useMemo(() => techniques.filter((technique) => {
    const matchesBelt = selectedBelts.size === 0 || selectedBelts.has(technique.beltLevel)
    const matchesType = selectedTypes.size === 0 || selectedTypes.has(technique.type)
    return matchesBelt && matchesType
  }), [selectedBelts, selectedTypes, techniques])

  const hasActiveFilters = selectedBelts.size > 0 || selectedTypes.size > 0

  const resetFilters = () => {
    setSelectedBelts(new Set())
    setSelectedTypes(new Set())
  }

  const openTechnique = async (technique: Technique) => {
    setSelectedTechnique(technique)
    try {
      const response = await axios.get<Technique>(`/api/techniques/${technique.id}`)
      setSelectedTechnique(response.data)
    } catch (error) {
      console.error('Impossible de renouveler les accès aux médias :', error)
    }
  }

  if (loading) {
    return (
      <div className="empty-state" role="status">
        <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
        <p className="mt-4 text-sm font-semibold text-gray-600">Chargement des techniques…</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Progression technique"
        title="Bibliothèque des techniques"
        description="Retrouvez les mouvements enseignés au club, classés par niveau et enrichis de leurs supports pédagogiques."
        icon={<BookOpen />}
      />

      {techniques.length > 0 && (
        <section className="technique-filter-bar" aria-labelledby="technique-filter-title">
          <div className="technique-filter-header">
            <div className="technique-filter-heading">
              <span className="technique-filter-icon" aria-hidden="true"><SlidersHorizontal /></span>
              <div>
                <h2 id="technique-filter-title">Filtrer les techniques</h2>
                <p>Sélectionnez plusieurs niveaux ou types pour affiner la bibliothèque.</p>
              </div>
            </div>
            <div className="technique-filter-summary" aria-live="polite" aria-atomic="true">
              <span><strong>{filteredTechniques.length}</strong> sur {techniques.length}</span>
              {hasActiveFilters && (
                <button type="button" className="technique-filter-reset" onClick={resetFilters}>
                  <RotateCcw aria-hidden="true" /> Réinitialiser
                </button>
              )}
            </div>
          </div>

          <div className="technique-filter-groups">
            <fieldset className="technique-filter-group">
              <legend>Niveau de ceinture requis</legend>
              <div className="technique-filter-options">
                {beltOptions.map((belt) => {
                  const isSelected = selectedBelts.has(belt.value)
                  const count = techniques.filter((technique) => technique.beltLevel === belt.value).length
                  return (
                    <label key={belt.value} className={`technique-filter-option ${isSelected ? 'technique-filter-option-active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => setSelectedBelts((current) => toggleSelection(current, belt.value))}
                      />
                      <span className={`technique-filter-swatch ${getBeltClass(belt.value)}`} aria-hidden="true" />
                      <span>{belt.label}</span>
                      <span className="technique-filter-count" aria-label={`${count} technique${count > 1 ? 's' : ''}`}>{count}</span>
                    </label>
                  )
                })}
              </div>
            </fieldset>

            <fieldset className="technique-filter-group">
              <legend>Type de technique</legend>
              <div className="technique-filter-options">
                {techniqueTypeOptions.map((type) => {
                  const isSelected = selectedTypes.has(type.value)
                  const count = techniques.filter((technique) => technique.type === type.value).length
                  return (
                    <label key={type.value} className={`technique-filter-option ${isSelected ? 'technique-filter-option-active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => setSelectedTypes((current) => toggleSelection(current, type.value))}
                      />
                      <span>{type.label}</span>
                      <span className="technique-filter-count" aria-label={`${count} technique${count > 1 ? 's' : ''}`}>{count}</span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
          </div>
        </section>
      )}

      {filteredTechniques.length > 0 ? (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filteredTechniques.map((technique) => (
            <button
              key={technique.id}
              type="button"
              className="card technique-card min-h-[300px]"
              onClick={() => void openTechnique(technique)}
              aria-label={`Voir les détails de la technique ${technique.name}`}
            >
              <span className="relative z-[1] flex h-full flex-col">
                {technique.media.imageUrl && (
                  <span className="mb-5 block overflow-hidden rounded-[1.1rem] bg-emerald-950/5">
                    <img
                      src={technique.media.imageUrl}
                      alt=""
                      className="h-40 w-full object-cover"
                    />
                  </span>
                )}
                <span className="flex items-start justify-between gap-3">
                  <span className="card-icon" aria-hidden="true"><BookOpen /></span>
                  <span className="support-chip">{getTypeLabel(technique.type)}</span>
                </span>

                <span className="mt-5 block text-xl font-extrabold leading-tight text-gray-900">
                  {technique.name}
                </span>
                <span className="mt-2 line-clamp-3 text-sm leading-6 text-gray-600">
                  {technique.description}
                </span>

                <span className="mb-3 mt-4 flex flex-wrap gap-1.5">
                  <span className={`belt-chip ${getBeltClass(technique.beltLevel)}`}>
                    Ceinture {getBeltLabel(technique.beltLevel).toLowerCase()}
                  </span>
                  {technique.media.imageUrl && (
                    <span className="support-chip"><ImageIcon className="h-3.5 w-3.5" aria-hidden="true" /> Photo</span>
                  )}
                  {technique.media.videoUrl && (
                    <span className="support-chip"><PlayCircle className="h-3.5 w-3.5" aria-hidden="true" /> Vidéo</span>
                  )}
                  {technique.media.pdfUrl && (
                    <span className="support-chip"><FileText className="h-3.5 w-3.5" aria-hidden="true" /> PDF</span>
                  )}
                </span>

                <span className="mt-auto flex items-center justify-between border-t border-primary-900/10 pt-4">
                  <span className="text-xs font-semibold text-gray-500">
                    {technique.media.imageUrl || technique.media.videoUrl || technique.media.pdfUrl ? 'Support pédagogique disponible' : 'Fiche descriptive'}
                  </span>
                  <span className="card-link-hint">Voir <ChevronRight aria-hidden="true" /></span>
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : techniques.length === 0 ? (
        <div className="empty-state">
          <BookOpen className="mx-auto h-14 w-14 text-gray-300" />
          <h3 className="mt-3 text-lg font-bold text-gray-900">Aucune technique</h3>
          <p className="mt-1 text-sm text-gray-500">Aucune technique n'est enregistrée pour le moment.</p>
        </div>
      ) : (
        <div className="empty-state" role="status">
          <SearchX className="mx-auto h-14 w-14 text-gray-300" />
          <h3 className="mt-3 text-lg font-bold text-gray-900">Aucune technique ne correspond</h3>
          <p className="mt-1 text-sm text-gray-500">Essayez de modifier votre sélection de ceintures ou de types.</p>
          <button type="button" className="btn-secondary mt-5" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Réinitialiser les filtres
          </button>
        </div>
      )}

      <TechniqueModal
        technique={selectedTechnique}
        isOpen={selectedTechnique !== null}
        onClose={() => setSelectedTechnique(null)}
      />
    </div>
  )
}

export default Techniques
